import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Plus, Trash2, Edit2, Save, X, Layers, FileText, Building2,
  ChevronRight, Copy, Download, Upload, CheckCircle2, AlertCircle,
  FolderOpen, Loader2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FrameworkBuilder = () => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [customFrameworks, setCustomFrameworks] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showControlDialog, setShowControlDialog] = useState(false);
  const [editingControl, setEditingControl] = useState(null);

  // Form state for new framework
  const [newFramework, setNewFramework] = useState({
    name: '',
    code: '',
    description: '',
    version: '1.0',
    industry: '',
    controls: []
  });

  // Form state for new/edit control
  const [controlForm, setControlForm] = useState({
    control_id: '',
    control_name: '',
    description: '',
    domain: '',
    guidance: '',
    evidence_requirements: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch templates and custom frameworks in parallel
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      const [templatesRes, frameworksRes] = await Promise.all([
        axios.get(`${API_URL}/api/grc/custom-frameworks/templates`, { headers }),
        axios.get(`${API_URL}/api/grc/custom-frameworks/list`, { headers }).catch(() => 
          // Fallback: some routes may redirect, so try without trailing operations
          axios.get(`${API_URL}/api/grc/custom-frameworks`, { headers, maxRedirects: 5 })
        )
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      setCustomFrameworks(frameworksRes.data?.frameworks || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load framework data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromTemplate = async (templateId) => {
    setIsCreating(true);
    try {
      const template = templates.find(t => t.id === templateId);
      const code = `CUSTOM-${template.id.toUpperCase().replace(/-/g, '')}`.substring(0, 20);
      
      const response = await axios.post(
        `${API_URL}/api/grc/custom-frameworks/from-template/${templateId}`,
        null,
        {
          params: { name: template.name, code },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      toast.success('Framework created from template');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create framework');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateFramework = async () => {
    if (!newFramework.name || !newFramework.code) {
      toast.error('Name and code are required');
      return;
    }

    setIsCreating(true);
    try {
      await axios.post(`${API_URL}/api/grc/custom-frameworks`, newFramework, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Custom framework created');
      setShowCreateDialog(false);
      setNewFramework({
        name: '',
        code: '',
        description: '',
        version: '1.0',
        industry: '',
        controls: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create framework');
    } finally {
      setIsCreating(false);
    }
  };

  const handleViewFramework = async (frameworkId) => {
    try {
      const response = await axios.get(`${API_URL}/api/grc/custom-frameworks/${frameworkId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSelectedFramework(response.data);
    } catch (error) {
      toast.error('Failed to load framework details');
    }
  };

  const handleDeleteFramework = async (frameworkId) => {
    if (!confirm('Are you sure you want to delete this framework and all its controls?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/grc/custom-frameworks/${frameworkId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Framework deleted');
      setSelectedFramework(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete framework');
    }
  };

  const handleAddControl = async () => {
    if (!controlForm.control_id || !controlForm.control_name || !controlForm.domain) {
      toast.error('Control ID, name, and domain are required');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/grc/custom-frameworks/${selectedFramework.framework.id}/controls`,
        controlForm,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success('Control added');
      setShowControlDialog(false);
      resetControlForm();
      handleViewFramework(selectedFramework.framework.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add control');
    }
  };

  const handleUpdateControl = async () => {
    try {
      await axios.put(
        `${API_URL}/api/grc/custom-frameworks/${selectedFramework.framework.id}/controls/${editingControl.id}`,
        controlForm,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success('Control updated');
      setShowControlDialog(false);
      setEditingControl(null);
      resetControlForm();
      handleViewFramework(selectedFramework.framework.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update control');
    }
  };

  const handleDeleteControl = async (controlId) => {
    if (!confirm('Delete this control?')) return;

    try {
      await axios.delete(
        `${API_URL}/api/grc/custom-frameworks/${selectedFramework.framework.id}/controls/${controlId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success('Control deleted');
      handleViewFramework(selectedFramework.framework.id);
    } catch (error) {
      toast.error('Failed to delete control');
    }
  };

  const resetControlForm = () => {
    setControlForm({
      control_id: '',
      control_name: '',
      description: '',
      domain: '',
      guidance: '',
      evidence_requirements: []
    });
  };

  const openEditControl = (control) => {
    setEditingControl(control);
    setControlForm({
      control_id: control.control_id,
      control_name: control.control_name,
      description: control.description || '',
      domain: control.domain,
      guidance: control.guidance || '',
      evidence_requirements: control.evidence_requirements || []
    });
    setShowControlDialog(true);
  };

  // Group controls by domain
  const controlsByDomain = selectedFramework?.controls?.reduce((acc, ctrl) => {
    const domain = ctrl.domain || 'Uncategorized';
    if (!acc[domain]) acc[domain] = [];
    acc[domain].push(ctrl);
    return acc;
  }, {}) || {};

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="framework-builder">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Layers className="w-7 h-7 text-purple-600" />
            Custom Framework Builder
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create and manage custom compliance frameworks
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-framework-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Framework
        </Button>
      </div>

      {selectedFramework ? (
        /* Framework Detail View */
        <div className="space-y-6">
          <Button variant="ghost" onClick={() => setSelectedFramework(null)} className="mb-4">
            <ChevronRight className="w-4 h-4 mr-2 rotate-180" />
            Back to Frameworks
          </Button>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {selectedFramework.framework.name}
                    <Badge variant="outline">{selectedFramework.framework.code}</Badge>
                  </CardTitle>
                  <CardDescription>{selectedFramework.framework.description}</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      resetControlForm();
                      setEditingControl(null);
                      setShowControlDialog(true);
                    }}
                    data-testid="add-control-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Control
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteFramework(selectedFramework.framework.id)}
                    data-testid="delete-framework-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
              <div className="flex gap-4 mt-4 text-sm text-muted-foreground">
                <span>Version: {selectedFramework.framework.version}</span>
                <span>Industry: {selectedFramework.framework.industry || 'General'}</span>
                <span>Controls: {selectedFramework.controls?.length || 0}</span>
                <span>Domains: {Object.keys(controlsByDomain).length}</span>
              </div>
            </CardHeader>
          </Card>

          {/* Controls by Domain */}
          {Object.entries(controlsByDomain).map(([domain, controls]) => (
            <Card key={domain} data-testid={`domain-card-${domain}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" />
                  {domain}
                  <Badge variant="secondary">{controls.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-32">ID</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="hidden md:table-cell">Status</TableHead>
                      <TableHead className="w-24 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {controls.map(control => (
                      <TableRow key={control.id}>
                        <TableCell className="font-mono text-sm">{control.control_id}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{control.control_name}</p>
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {control.description}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Badge variant={
                            control.status === 'implemented' ? 'default' :
                            control.status === 'in_progress' ? 'secondary' : 'outline'
                          }>
                            {control.status || 'Not Implemented'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditControl(control)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteControl(control.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}

          {Object.keys(controlsByDomain).length === 0 && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="font-medium">No Controls Yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add controls to your framework to get started
                  </p>
                  <Button
                    className="mt-4"
                    onClick={() => {
                      resetControlForm();
                      setEditingControl(null);
                      setShowControlDialog(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Control
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        /* Templates and Custom Frameworks List */
        <Tabs defaultValue="templates" className="space-y-4">
          <TabsList>
            <TabsTrigger value="templates" data-testid="tab-templates">
              Templates
            </TabsTrigger>
            <TabsTrigger value="custom" data-testid="tab-custom">
              My Frameworks ({customFrameworks.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="templates" className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Start with a pre-built template and customize it for your needs
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {templates.map(template => (
                <Card key={template.id} className="hover:shadow-lg transition-shadow" data-testid={`template-${template.id}`}>
                  <CardHeader>
                    <CardTitle className="text-base">{template.name}</CardTitle>
                    <CardDescription className="text-xs">
                      {template.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Industry</span>
                        <Badge variant="outline">{template.industry}</Badge>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Controls</span>
                        <span className="font-medium">{template.controls_count}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {template.domains?.slice(0, 3).map(domain => (
                          <Badge key={domain} variant="secondary" className="text-xs">
                            {domain}
                          </Badge>
                        ))}
                      </div>
                      <Button
                        className="w-full mt-2"
                        variant="outline"
                        onClick={() => handleCreateFromTemplate(template.id)}
                        disabled={isCreating}
                      >
                        {isCreating ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Copy className="w-4 h-4 mr-2" />
                        )}
                        Use Template
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="custom" className="space-y-4">
            {customFrameworks.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-12">
                    <Layers className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-medium">No Custom Frameworks</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Create a framework from scratch or use a template
                    </p>
                    <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Framework
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {customFrameworks.map(fw => (
                  <Card
                    key={fw.id}
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => handleViewFramework(fw.id)}
                    data-testid={`custom-framework-${fw.code}`}
                  >
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">{fw.name}</CardTitle>
                        <Badge variant="outline">{fw.code}</Badge>
                      </div>
                      <CardDescription className="text-xs line-clamp-2">
                        {fw.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Controls</span>
                          <span className="font-medium">{fw.total_controls}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Domains</span>
                          <span className="font-medium">{fw.total_domains}</span>
                        </div>
                        {fw.industry && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Industry</span>
                            <Badge variant="secondary" className="text-xs">{fw.industry}</Badge>
                          </div>
                        )}
                        {fw.documentation_url && (
                          <a 
                            href={fw.documentation_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs mt-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3 w-3" />
                            Official Documentation
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Create Framework Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Custom Framework</DialogTitle>
            <DialogDescription>
              Define your own compliance framework from scratch
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Framework Name *</Label>
                <Input
                  placeholder="e.g., Internal Security Policy"
                  value={newFramework.name}
                  onChange={(e) => setNewFramework({ ...newFramework, name: e.target.value })}
                  data-testid="framework-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Code * (uppercase, no spaces)</Label>
                <Input
                  placeholder="e.g., INT-SEC"
                  value={newFramework.code}
                  onChange={(e) => setNewFramework({ 
                    ...newFramework, 
                    code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '')
                  })}
                  maxLength={20}
                  data-testid="framework-code-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe the purpose and scope of this framework"
                value={newFramework.description}
                onChange={(e) => setNewFramework({ ...newFramework, description: e.target.value })}
                rows={3}
                data-testid="framework-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Version</Label>
                <Input
                  placeholder="1.0"
                  value={newFramework.version}
                  onChange={(e) => setNewFramework({ ...newFramework, version: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Industry</Label>
                <Select
                  value={newFramework.industry}
                  onValueChange={(v) => setNewFramework({ ...newFramework, industry: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Technology">Technology</SelectItem>
                    <SelectItem value="Healthcare">Healthcare</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                    <SelectItem value="Retail">Retail</SelectItem>
                    <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="Government">Government</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateFramework} disabled={isCreating} data-testid="save-framework-btn">
              {isCreating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Create Framework
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Control Dialog */}
      <Dialog open={showControlDialog} onOpenChange={setShowControlDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingControl ? 'Edit Control' : 'Add Control'}</DialogTitle>
            <DialogDescription>
              {editingControl ? 'Update the control details' : 'Add a new control to your framework'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Control ID *</Label>
                <Input
                  placeholder="e.g., AC-01"
                  value={controlForm.control_id}
                  onChange={(e) => setControlForm({ ...controlForm, control_id: e.target.value })}
                  disabled={!!editingControl}
                  data-testid="control-id-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Domain *</Label>
                <Input
                  placeholder="e.g., Access Control"
                  value={controlForm.domain}
                  onChange={(e) => setControlForm({ ...controlForm, domain: e.target.value })}
                  data-testid="control-domain-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Control Name *</Label>
              <Input
                placeholder="e.g., Multi-Factor Authentication"
                value={controlForm.control_name}
                onChange={(e) => setControlForm({ ...controlForm, control_name: e.target.value })}
                data-testid="control-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe what this control requires"
                value={controlForm.description}
                onChange={(e) => setControlForm({ ...controlForm, description: e.target.value })}
                rows={3}
                data-testid="control-description-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Implementation Guidance</Label>
              <Textarea
                placeholder="Provide guidance on how to implement this control"
                value={controlForm.guidance}
                onChange={(e) => setControlForm({ ...controlForm, guidance: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowControlDialog(false);
              setEditingControl(null);
              resetControlForm();
            }}>
              Cancel
            </Button>
            <Button
              onClick={editingControl ? handleUpdateControl : handleAddControl}
              data-testid="save-control-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {editingControl ? 'Update Control' : 'Add Control'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FrameworkBuilder;
